import { useState } from 'react'
import { Alert, Button, Card, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { PermissionNotice } from '@/shared/ui/PermissionNotice'
import { RowActions } from '@/shared/ui/table/RowActions'
import type { OperationResult } from '@/shared/api/operationResult'
import { LeaveAdjustModal } from './LeaveAdjustModal'
import { LeaveDecisionModal, type LeaveAction } from './LeaveDecisionModal'
import { LeaveRequestModal } from './LeaveRequestModal'
import {
  canCancelLeave,
  defaultLeaveYear,
  fetchLeaveBalance,
  fetchLeaveTypes,
  fetchPersonLeaveRequests,
  isPending,
  pendingOverBalance,
  type LeaveBalance,
  type LeaveRequest,
} from './leaveApi'
import { LEAVE_STATE_COLOR } from './leaveState'

interface Props {
  personId: string
}

/**
 * Xodimning ta'til balansi va so'rovlari.
 *
 * <h2>⚠️ `pendingDays` QOLDIQDAN AYRILMAGAN — va bu KO'RSATILADI</h2>
 * Server formulasi: `qoldiq = huquq + tuzatish − TASDIQLANGAN kunlar`.
 * Kutayotgan so'rovlar unga kirmaydi. Ya'ni «qoldiq 10 kun» ko'rinib
 * turib tasdiqlash 409 bilan rad etilishi mumkin. Shuning uchun
 * kutayotgan kunlar alohida ustunda turadi va qoldiqdan oshsa
 * OGOHLANTIRISH chiqadi — sabab serverdan kelgunga qadar ekranda
 * bo'ladi.
 *
 * <h2>⚠️ YIL — TENANT ZONASIDA</h2>
 * Standart qiymat `defaultLeaveYear()` dan, ya'ni tenant kalendaridagi
 * joriy yil. Serverga u HAR DOIM yuboriladi: bo'sh qolsa server O'Z
 * yilini qo'yadi va 31-dekabrda ular farq qilishi mumkin.
 *
 * <h2>⚠️ SO'ROV SHU YERDAN YOZILADI</h2>
 * `POST /persons/{id}/leave-requests` xodim id'sini talab qiladi, ya'ni
 * tabiiy joyi — kartochka. Ro'yxat sahifasi esa QARORLAR uchun: u yerda
 * hamma xodimning kutayotgan so'rovlari bir joyda ko'rinadi.
 */
export function LeaveCard({ personId }: Props) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canView = can('hr.leave.view')
  const canRequest = can('hr.leave.request')
  const canApprove = can('hr.leave.approve')

  const [year, setYear] = useState(() => defaultLeaveYear())
  const [requestOpen, setRequestOpen] = useState(false)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [decision, setDecision] = useState<{ action: LeaveAction; request: LeaveRequest } | null>(
    null,
  )
  const [result, setResult] = useState<OperationResult | null>(null)

  const balance = useQuery({
    queryKey: queryKeys.hr.leaveBalance(personId, year),
    queryFn: () => fetchLeaveBalance(personId, year),
    enabled: canView && personId !== '',
  })

  const requests = useQuery({
    queryKey: [...queryKeys.hr.leaveRequests, personId] as const,
    queryFn: () => fetchPersonLeaveRequests(personId),
    enabled: canView && personId !== '',
  })

  const types = useQuery({
    queryKey: queryKeys.hr.leaveTypes(true),
    queryFn: () => fetchLeaveTypes(true),
    enabled: canView && (canRequest || canApprove),
    staleTime: 5 * 60_000,
  })

  if (!canView) {
    return (
      <Card title={t('hr.leave.title')}>
        <PermissionNotice
          permission="hr.leave.view"
          title={t('hr.leave.hiddenTitle')}
          reason={t('hr.leave.hiddenReason')}
        />
      </Card>
    )
  }

  const balances = balance.data ?? []
  const rows = requests.data ?? []
  const warned = balances.filter(pendingOverBalance)
  const balanceTypes = (types.data ?? []).filter((type) => type.usesBalance)

  const balanceColumns: ColumnsType<LeaveBalance> = [
    {
      // Balans ro'yxati sahifalanmaydi — ta'til turlari soni o'nlab
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{index + 1}</Typography.Text>
      ),
    },
    {
      title: t('hr.leave.type'),
      key: 'type',
      render: (_value, row) => row.leaveTypeName ?? row.leaveTypeCode ?? '—',
    },
    {
      title: t('hr.leave.entitled'),
      key: 'entitled',
      align: 'right',
      // ⚠️ `null` = cheklovsiz tur. `0` deb ko'rsatish teskari
      // ma'noni berardi — «umuman huquq yo'q».
      render: (_value, row) =>
        row.entitledDays === null ? t('hr.leave.unlimited') : row.entitledDays,
    },
    { title: t('hr.leave.adjusted'), dataIndex: 'adjustedDays', key: 'adjusted', align: 'right' },
    { title: t('hr.leave.used'), dataIndex: 'usedDays', key: 'used', align: 'right' },
    {
      title: t('hr.leave.pending'),
      key: 'pending',
      align: 'right',
      render: (_value, row) =>
        pendingOverBalance(row) ? (
          <Tooltip title={t('hr.leave.pendingOverHint')}>
            <Tag color="gold">{row.pendingDays}</Tag>
          </Tooltip>
        ) : (
          row.pendingDays
        ),
    },
    {
      title: t('hr.leave.remaining'),
      key: 'remaining',
      align: 'right',
      render: (_value, row) =>
        row.remainingDays === null ? (
          t('hr.leave.unlimited')
        ) : (
          <Typography.Text strong>{row.remainingDays}</Typography.Text>
        ),
    },
  ]

  const requestColumns: ColumnsType<LeaveRequest> = [
    {
      // So'rovlar ro'yxati ham sahifalanmaydi (`pagination={false}`)
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{index + 1}</Typography.Text>
      ),
    },
    {
      title: t('hr.leave.type'),
      key: 'type',
      render: (_value, row) => (
        <Space size={6} wrap>
          <span>{row.leaveTypeName ?? row.leaveTypeCode ?? '—'}</span>
          {row.paid ? <Tag color="blue">{t('hr.leave.paid')}</Tag> : null}
        </Space>
      ),
    },
    {
      title: t('hr.leave.range'),
      key: 'range',
      render: (_value, row) => `${row.startsOn} … ${row.endsOn}`,
    },
    { title: t('hr.leave.days'), dataIndex: 'days', key: 'days', align: 'right' },
    {
      title: t('hr.leave.state'),
      key: 'state',
      render: (_value, row) => (
        <Tag color={LEAVE_STATE_COLOR[row.state]}>{t(`leaveState.${row.state}`)}</Tag>
      ),
    },
    {
      title: t('hr.leave.reason'),
      key: 'reason',
      render: (_value, row) =>
        row.reason === null ? '—' : <ExpandableHint text={row.reason} />,
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_value, row) => (
        /*
          ⚠️ SHART VA HUQUQ AYNAN SAQLANDI, faqat joyi o'zgardi:
          tasdiqlash/rad etish FAQAT kutayotgan so'rovda (`isPending`),
          bekor qilish esa FAQAT `canCancelLeave` ruxsat bergan holatda
          (`leaveApi` da: boshlanmagan yoki hali kutayotgan so'rov).
          Shartni yumshatish tasdiqlangan ta'tilni ham "bekor qilinadi"
          deb ko'rsatib, serverdan 409 oldirardi.
        */
        <RowActions
          items={[
            {
              key: 'approve',
              label: t('hr.leave.action.APPROVE.submit'),
              onClick: () => setDecision({ action: 'APPROVE', request: row }),
              permission: 'hr.leave.approve',
              hidden: !isPending(row),
            },
            {
              key: 'reject',
              label: t('hr.leave.action.REJECT.submit'),
              onClick: () => setDecision({ action: 'REJECT', request: row }),
              permission: 'hr.leave.approve',
              hidden: !isPending(row),
              danger: true,
            },
            {
              key: 'cancel',
              label: t('hr.leave.action.CANCEL.submit'),
              onClick: () => setDecision({ action: 'CANCEL', request: row }),
              permission: 'hr.leave.request',
              hidden: !canCancelLeave(row),
              divider: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <Card
      title={t('hr.leave.title')}
      extra={
        <Space wrap>
          <Select<number>
            size="small"
            value={year}
            onChange={setYear}
            style={{ width: 110 }}
            options={[0, 1, 2].map((back) => {
              const value = defaultLeaveYear() - back
              return { value, label: String(value) }
            })}
          />
          {canApprove ? (
            <Button size="small" onClick={() => setAdjustOpen(true)}>
              {t('hr.leave.adjustAction')}
            </Button>
          ) : null}
          {canRequest ? (
            <Button size="small" type="primary" onClick={() => setRequestOpen(true)}>
              {t('hr.leave.requestAction')}
            </Button>
          ) : null}
        </Space>
      }
    >
      {result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={result}
            closable
            onClose={() => setResult(null)}
          />
        </div>
      ) : null}

      {balance.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(balance.error, t)}
        />
      ) : null}

      {warned.length === 0 ? null : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('hr.leave.pendingOverTitle')}
          description={t('hr.leave.pendingOverText')}
        />
      )}

      {/*
        ⚠️ «Ta'til turi yo'q» xulosasi FAQAT `isSuccess` da
        (`guards.mjs` 3-bo'limi). Katalog ataylab ekilmagan, ya'ni bu
        holat YANGI tashkilotda ODATIY — va u yo'l ko'rsatishi kerak,
        ayblamasligi.
      */}
      {balance.isSuccess && balances.length === 0 ? (
        <EmptyState title={t('hr.leave.emptyBalance')} description={t('hr.leave.emptyBalanceHint')} />
      ) : (
        <Table<LeaveBalance>
          rowKey={(row) => row.leaveTypeId}
          columns={balanceColumns}
          dataSource={[...balances]}
          loading={balance.isPending}
          pagination={false}
          size="small"
          scroll={{ x: 720 }}
        />
      )}

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        {t('hr.leave.requestsTitle')}
      </Typography.Title>

      <Table<LeaveRequest>
        rowKey={(row) => row.id}
        columns={requestColumns}
        dataSource={[...rows]}
        loading={requests.isPending}
        locale={requests.isError ? { emptyText: translateError(requests.error, t) } : undefined}
        pagination={false}
        size="small"
        scroll={{ x: 900 }}
      />

      <LeaveRequestModal
        open={requestOpen}
        personId={personId}
        types={types.data ?? []}
        onClose={() => setRequestOpen(false)}
      />

      <LeaveAdjustModal
        open={adjustOpen}
        personId={personId}
        year={year}
        types={balanceTypes}
        onClose={() => setAdjustOpen(false)}
      />

      {decision === null ? null : (
        <LeaveDecisionModal
          action={decision.action}
          request={decision.request}
          onClose={() => setDecision(null)}
          onDone={setResult}
        />
      )}
    </Card>
  )
}
